import { Button, Modal } from 'react-daisyui';
import { Trans, useTranslation } from 'react-i18next';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { resolveRetentionOfferPriceView } from '../../RetentionOfferModal/RetentionOfferModal.functions';
import { tPriceTooHighConfirmationModalProps } from '../ConfirmationModal.types';

/**
 * Success screen for an accepted discount. Its terms are the applied offer's:
 * the percent and months always, the prices only when the server priced the
 * subscription exactly.
 */
export default function PriceTooHighConfirmationModal(
  props: tPriceTooHighConfirmationModalProps,
) {
  const {
    isOpen,
    offer,
    nextChargeDate,
    returnToNormalDate,
    formatDate,
    onClose,
  } = props;
  const { t, i18n } = useTranslation('settings/SubscriptionManager');
  const { currentUser } = useUserContext();
  const { subscription } = useSubscriptionContext();

  const planName = t(`page.Plans.${subscription.planCode}`);
  const priceView = resolveRetentionOfferPriceView({
    offer,
    locale: i18n.language,
  });

  return (
    <Modal open={isOpen} className="max-w-[631px] rounded-lg p-0">
      <Modal.Header className="flex flex-row justify-end p-4 pb-0 mb-0">
        <Button
          color="ghost"
          size="sm"
          shape="circle"
          onClick={onClose}
          aria-label={t('cancelSubscription.offer.close')}
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </Button>
      </Modal.Header>
      <Modal.Body className="pt-0 px-6 pb-6 font-sans">
        <div className="flex flex-col gap-6 items-center">
          {/* Success Icon */}
          <img
            src="/images/subscriptions/retention-success.svg"
            alt="Retention Success"
            className="w-[199px] h-auto"
          />

          {/* Heading */}
          <div className="flex flex-col gap-2 text-center">
            <h4 className="text-[28px] font-bold text-success">
              <Trans
                i18nKey="settings/SubscriptionManager:cancelSubscription.confirmation.heading"
                values={{ name: currentUser?.name.split(' ')[0] }}
              />
            </h4>
            <p className="text-lg font-bold">
              {t('cancelSubscription.confirmation.subheading')}
            </p>
          </div>

          {/* Subscription Details Card */}
          <div className="w-full bg-success/5 rounded py-6 px-8">
            <div className="flex flex-col gap-4">
              {/* Plan Name */}
              <div>
                <p className="text-lg">
                  {t('cancelSubscription.confirmation.plan')}:{' '}
                  <span className="font-bold">{planName}</span>
                </p>
              </div>

              {/* Pricing */}
              <div className="flex flex-col">
                {priceView && (
                  <>
                    {/* Normal Price */}
                    <div className="flex flex-row justify-between items-center">
                      <p className="text-lg text-neutral-500 w-3/5">
                        {t('cancelSubscription.confirmation.normalPrice')}
                      </p>
                      <p className="text-lg text-neutral-500 line-through flex-1">
                        {priceView.currentAmount}/
                        {t('page.SubscriptionPlans.perMonthly')}
                      </p>
                    </div>

                    {/* Discounted Price */}
                    <div className="flex flex-row justify-between items-center">
                      <p className="text-lg w-3/5">
                        {t('cancelSubscription.confirmation.discountedPrice')}
                      </p>
                      <p className="text-xl font-bold text-success flex-1">
                        {priceView.offerAmount}/
                        {t('page.SubscriptionPlans.perMonthly')}
                      </p>
                    </div>
                  </>
                )}

                {/* Discount */}
                <div className="flex flex-row justify-between items-center">
                  <p className="text-lg w-3/5">
                    {t('cancelSubscription.confirmation.discount')}
                  </p>
                  <p className="text-lg font-bold text-success flex-1">
                    {t('cancelSubscription.confirmation.discountValue', {
                      percent: offer.percentOff,
                    })}
                  </p>
                </div>

                {/* Duration */}
                <div className="flex flex-row justify-between items-center">
                  <p className="text-lg w-3/5">
                    {t('cancelSubscription.confirmation.duration')}
                  </p>
                  <p className="text-lg flex-1">
                    {t('cancelSubscription.confirmation.durationValue', {
                      months: offer.durationMonths,
                    })}
                  </p>
                </div>
              </div>

              {/* Separator */}
              <div className="border-t border-success" />

              {/* Next Charge */}
              <div className="flex flex-row justify-between items-center">
                <p className="text-lg w-3/5">
                  {t('cancelSubscription.confirmation.nextCharge')}
                </p>
                <p className="text-lg flex-1">{formatDate(nextChargeDate)}</p>
              </div>

              {/* Separator */}
              <div className="border-t border-success" />

              {/* Return to Normal Price */}
              <div className="flex">
                <p className="text-lg w-3/5">
                  {t('cancelSubscription.confirmation.returnToNormalDate', {
                    date: formatDate(returnToNormalDate),
                  })}
                </p>
                <p className="text-lg flex-1">
                  {priceView
                    ? t('cancelSubscription.confirmation.returnToNormalPrice', {
                        amount: priceView.currentAmount,
                      })
                    : t(
                        'cancelSubscription.confirmation.returnToNormalPriceUnpriced',
                      )}
                </p>
              </div>

              {/* Footer Message */}
              <p className="text-xs text-center">
                {t('cancelSubscription.confirmation.footerMessage')}
              </p>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
